<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import Badge from "@/design/primitives/Badge.vue";
import Icon from "@/design/primitives/Icon.vue";
import { navMessages } from "./i18n/nav";
import type { NavRow } from "./nav-items";

/**
 * Uma linha da lateral: um traço, um rótulo e, às vezes, um número.
 *
 * Saiu de dentro do `NavGroup` porque eram dois assuntos no mesmo arquivo — o
 * grupo trata de abrir e fechar, a linha trata de estar ou não estar onde se está.
 * O grupo não desenha mais nada por conta própria; ele empilha estas.
 *
 * ── POR QUE `custom`, E NÃO `exact-active-class` ─────────────────────────
 *
 * Porque havia DUAS ideias de "atual" convivendo aqui: o `exact-active-class` do
 * router para quase todas as linhas, e uma prop `active` para as que se
 * distinguem pela query (`/board?funil=`), que o router não compara. Duas ideias
 * pedem que cada estilo do estado ativo seja escrito duas vezes — e era isso que
 * obrigava `!bg-…` e `!text-…` a existir, para uma classe vencer a outra dentro
 * do mesmo atributo.
 *
 * Com o slot, o router continua respondendo (`isExactActive`) e a prop continua
 * podendo discordar, mas quem junta as duas é UMA função. Daí em diante o estado
 * é um booleano: as classes de ativo e de inativo são exclusivas, nenhuma precisa
 * de `!` para ganhar da outra, e o `aria-current` passa a ser dito de propósito
 * em vez de herdado.
 *
 * O `<a>` continua sendo um link de verdade — `href` real, clique do meio abre
 * noutra aba, e `navigate` já ignora o clique com Ctrl, Cmd, Shift ou Alt.
 */
const props = defineProps<{ row: NavRow; narrow?: boolean }>();

const emit = defineEmits<{ navigate: [] }>();

/** O rótulo já vem desenhado; a única frase daqui é a do leitor de tela. */
const { t } = useI18n({ useScope: "local", inheritLocale: true, messages: navMessages });

/** A prop manda; sem ela, quem sabe é o router. */
function on(routerSays: boolean): boolean {
	return props.row.active ?? routerSays;
}

/**
 * O `group` não é decoração: é o que deixa o ÍCONE reagir ao ponteiro que está
 * sobre a linha — ele cresce um décimo, como na V2.
 *
 * OS NÚMEROS SÃO OS DA V2, e não escolha desta tela: 32px de altura (`.45rem` de
 * folga em cima de um traço de 18px), 12px entre traço e rótulo, raio de 8px,
 * ícone de 18px e letra de 13,125px em peso 500. A V3 desenhava 36/10/12/16/14px
 * — diferenças pequenas que somadas faziam a mesma lista parecer outra ao lado
 * da V2. O `text-nav` é o tamanho nomeado em `tokens.css`, e é onde está escrito
 * por que ele vem em pixels.
 *
 * AS CORES também: `nav-fg` (#262626 / #d4d4d4) para a linha em repouso,
 * `nav-hover` sob o ponteiro, e o item atual num CINZA neutro (`nav-on`,
 * #f3f3f3 / branco a 8%) com o texto quase preto — nunca a pílula azul do
 * acento, que era o que mais separava as duas cascas lado a lado.
 */
// padrão Claude (dono, 25/10): 36px e raio de 12px; a gaveta do telefone sobe para 44px (`App.vue`).
const BASE =
	"group relative flex h-9 w-full items-center gap-3 whitespace-nowrap rounded-lg text-nav font-medium transition-colors";
const IDLE = "text-nav-fg hover:bg-nav-hover hover:text-nav-fg-strong";
const ON = "bg-nav-on text-nav-on-fg";

/**
 * O TRAÇO NÃO TEM TOM PRÓPRIO — ele herda a cor da linha, como na V2.
 *
 * Lá o `<component :is="item.icon">` não carrega classe de cor nenhuma: ícone e
 * rótulo saem no mesmo `#262626` (claro) ou `#d4d4d4` (escuro), e no item ativo
 * nos mesmos `#171717`/`#fafafa`. Aqui o traço vinha um degrau ABAIXO do texto
 * (`fg-muted` sob um rótulo `fg`), e ao lado da V2 isso lia-se como um menu
 * apagado — a queixa do dono em 09/09/2026, e o mesmo assunto do ajuste de
 * 26/08 ("as cores dos ícones podia ser um pouco mais fortes"), agora resolvido
 * pela raiz: um tom só para a linha inteira.
 *
 * 18px NOS DOIS ESTADOS, que é a medida única da V2. Os 20px do rail recolhido
 * (dono, 27/08: "um pouquinho maior e mais escuro") existiam para compensar um
 * traço que ficava claro demais sozinho; com a herança ele já vem escuro, e o
 * tamanho volta a ser o mesmo aberto ou fechado.
 */
// padrão Claude (dono, 25/10): o traço não salta mais sob o ponteiro — o hover da linha já diz.
const GLYPH = "shrink-0 size-[18px]";

/** O ponto do menu estreito, na cor do badge que ele substitui. */
const DOT: Record<NonNullable<NavRow["tone"]>, string> = {
	accent: "bg-accent",
	warning: "bg-warning",
	danger: "bg-danger",
	info: "bg-info",
};
</script>

<template>
	<RouterLink v-slot="{ href, navigate, isExactActive }" :to="row.to" custom>
		<a
			:href="href"
			:class="[BASE, narrow ? 'justify-center px-0' : 'px-3', on(isExactActive) ? ON : IDLE]"
			:aria-current="on(isExactActive) ? 'page' : undefined"
			:title="narrow ? row.label : undefined"
			@click="
				navigate($event);
				emit('navigate');
			"
		>
			<Icon v-if="row.icon" :name="row.icon" :class="GLYPH" />
			<!-- Sem ícone, as duas primeiras letras ocupam a MESMA coluna que ele —
			     senão o que o tenant criou fica alinhado num lugar e o que o produto
			     traz noutro, e a lista parece duas listas. Aberto ou estreito. -->
			<span
				v-else
				class="flex size-[18px] shrink-0 items-center justify-center text-3xs font-semibold uppercase"
				aria-hidden="true"
			>
				{{ row.initials }}
			</span>

			<span v-if="!narrow" class="truncate">{{ row.label }}</span>
			<Badge v-if="!narrow && row.badge" :tone="row.tone ?? 'accent'" class="ml-auto shrink-0">
				{{ row.badge > 99 ? "99+" : row.badge }}
			</Badge>
			<!-- Estreito não cabe o número, e some justamente o aviso pelo qual o
			     badge existe. Um ponto diz "tem coisa aí" no espaço que há — e na
			     mesma cor do número que ele substitui, senão recolher o menu apaga
			     também a diferença entre "cliente esperando" e "missão rodando". -->
			<span
				v-else-if="row.badge"
				class="absolute right-1 top-1 size-2 rounded-full"
				:class="DOT[row.tone ?? 'accent']"
				:aria-label="t('nav.waitingIn', { n: row.badge, group: row.label })"
			/>
		</a>
	</RouterLink>
</template>
